import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { BLISSNET_LIVE_MATCH, type BlissNetLiveMatchDetail } from '@/contexts/blissLiveSync/blissnetLiveEvents';
import { toast } from 'sonner';
import MatchCelebration from '../components/matches/MatchCelebration';
import { navigateToFriendChat } from '../services/discover/chatNavigationService';
import { supabase } from '@/integrations/supabase/client';

interface MatchedUser {
  id: string;
  name: string;
  photo: string;
}

interface QueuedMatch {
  matchId: string;
  matchedUser: MatchedUser;
}

interface MatchNotificationContextType {
  showMatchCelebration: (matchedUser: MatchedUser, matchId?: string) => void;
  hideMatchCelebration: (matchIdToSave?: string) => void;
  isMatchCelebrationVisible: boolean;
}

const MatchNotificationContext = createContext<MatchNotificationContextType | undefined>(undefined);

export const useMatchNotification = () => {
  const context = useContext(MatchNotificationContext);
  if (!context) {
    throw new Error('useMatchNotification must be used within a MatchNotificationProvider');
  }
  return context;
};

interface MatchNotificationProviderProps {
  children: React.ReactNode;
}

export const MatchNotificationProvider: React.FC<MatchNotificationProviderProps> = ({ children }) => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [showMatchNotification, setShowMatchNotification] = useState(false);
  const [matchedUser, setMatchedUser] = useState<MatchedUser | null>(null);
  const [currentMatchId, setCurrentMatchId] = useState<string | null>(null);
  const [unseenQueue, setUnseenQueue] = useState<QueuedMatch[]>([]);
  const mountCheckDone = useRef(false);
  const lastUserId = useRef<string | null>(null);
  const currentMatchIdRef = useRef<string | null>(null);
  const unseenQueueRef = useRef<QueuedMatch[]>([]);
  /** Dedupe modal when live + mount-check both fire for the same match id */
  const shownMatchIdsRef = useRef<Set<string>>(new Set());
  currentMatchIdRef.current = currentMatchId;
  unseenQueueRef.current = unseenQueue;

  /** V323: Mark match as seen in DB. Strict guard prevents 406; .limit(1)+array extraction prevents PGRST116. */
  const markMatchAsSeen = useCallback(async (matchId?: string) => {
    if (!matchId || typeof matchId !== 'string' || matchId.trim() === '') {
      console.warn('⚠️ markMatchAsSeen skipped: Invalid matchId');
      return;
    }

    if (!user?.id) return;

    try {
      const { data: matchRows, error: fetchError } = await supabase
        .from('matches')
        .select('user1_id, user2_id')
        .eq('id', matchId)
        .limit(1);

      const match = matchRows?.[0];

      if (fetchError || !match) return;

      const updatePayload = match.user1_id === user.id
        ? { user1_seen_at: new Date().toISOString() }
        : { user2_seen_at: new Date().toISOString() };

      await supabase.from('matches').update(updatePayload).eq('id', matchId);
      console.log(`✅ Stamped match ${matchId} as seen`);
    } catch (err) {
      console.error('🔥 Error marking match as seen:', err);
    }
  }, [user?.id]);

  /** V323: Show a match with optional matchId for DB tracking */
  const showMatch = useCallback((matchedUserData: MatchedUser, matchId?: string) => {
    if (matchId && shownMatchIdsRef.current.has(matchId)) return;
    // 🚀 INJECT CACHE WHEN MATCH SHOWN (covers mount/queue path; Swiper gets it from showMatchCelebration)
    if (typeof window !== 'undefined' && matchedUserData?.id) {
      window.dispatchEvent(
        new CustomEvent('blissnet-inject-user', {
          detail: {
            userId: matchedUserData.id,
            name: matchedUserData.name || 'Someone',
            photo_urls: matchedUserData.photo ? [matchedUserData.photo] : []
          }
        })
      );
      window.dispatchEvent(new CustomEvent('blissnet-new-match-received'));
    }
    if (matchId) shownMatchIdsRef.current.add(matchId);
    setMatchedUser(matchedUserData);
    setCurrentMatchId(matchId ?? null);
    setShowMatchNotification(true);
  }, []);

  /** V323: On close, persist matchId to DB and show next in queue if any.
   * Fire DB update BEFORE state change - avoids race when "Start Chatting" unmounts during nav. */
  const hideMatchCelebration = useCallback((matchIdToSave?: string) => {
    const firstInQueue = unseenQueueRef.current[0]?.matchId;
    const idToSave = matchIdToSave ?? currentMatchIdRef.current ?? firstInQueue;

    if (idToSave && user?.id) {
      console.log('🛡️ Marking match as seen in DB:', idToSave);
      markMatchAsSeen(idToSave);
    } else if (!idToSave) {
      console.error('❌ FAILED TO SAVE: No Match ID available.');
    }

    // 🚀 FORCE CACHE & CHATLIST REFRESH BEFORE CLOSING (Fixes "Unknown User" for Swiper)
    if (typeof window !== 'undefined' && matchedUser?.id) {
      window.dispatchEvent(
        new CustomEvent('blissnet-inject-user', {
          detail: {
            userId: matchedUser.id,
            name: matchedUser.name,
            photo_urls: matchedUser.photo ? [matchedUser.photo] : []
          }
        })
      );
      window.dispatchEvent(new CustomEvent('blissnet-new-match-received'));
    }

    let rest = unseenQueueRef.current.slice(1);
    while (rest.length > 0 && rest[0].matchId && shownMatchIdsRef.current.has(rest[0].matchId)) {
      rest = rest.slice(1);
    }
    setUnseenQueue(rest);
    if (rest.length > 0) {
      const next = rest[0];
      showMatch(next.matchedUser, next.matchId);
    } else {
      setShowMatchNotification(false);
      setMatchedUser(null);
      setCurrentMatchId(null);
    }
  }, [user?.id, markMatchAsSeen, matchedUser, showMatch]);

  // Phase A: match realtime owned by BlissLiveSyncProvider; UI via blissnet-live-match only
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<BlissNetLiveMatchDetail>;
      const d = ce.detail;
      if (!d?.matchId || !d?.otherUserId) return;
      const matchedUserData: MatchedUser = {
        id: d.otherUserId,
        name: d.otherUserName,
        photo: d.otherUserPhoto
      };
      showMatch(matchedUserData, d.matchId);
      toast.success(`🎉 New Tie! You and ${d.otherUserName} are now connected.`);
    };
    window.addEventListener(BLISSNET_LIVE_MATCH, handler as EventListener);
    return () => window.removeEventListener(BLISSNET_LIVE_MATCH, handler as EventListener);
  }, [showMatch]);

  const showMatchCelebration = useCallback((matchedUserData: MatchedUser, matchId?: string) => {
    // 🚀 AGGRESSIVELY INJECT CACHE THE MOMENT THE MATCH HAPPENS (Fixes "Unknown User" for Swiper)
    if (typeof window !== 'undefined' && matchedUserData?.id) {
      window.dispatchEvent(
        new CustomEvent('blissnet-inject-user', {
          detail: {
            userId: matchedUserData.id,
            name: matchedUserData.name || 'Someone',
            photo_urls: matchedUserData.photo ? [matchedUserData.photo] : []
          }
        })
      );
      window.dispatchEvent(new CustomEvent('blissnet-new-match-received'));
    }
    showMatch(matchedUserData, matchId);
  }, [showMatch]);

  // V323: On app open, fetch active matches and show any unseen ones (offline catch-up)
  useEffect(() => {
    if (!user?.id) return;
    if (lastUserId.current !== user.id) {
      lastUserId.current = user.id;
      mountCheckDone.current = false;
    }
    if (mountCheckDone.current) return;
    mountCheckDone.current = true;

    let isMounted = true;

    (async () => {
      try {
        const { data: allMatches, error } = await supabase
          .from('matches')
          .select('id, user1_id, user2_id, user1_seen_at, user2_seen_at')
          .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
          .eq('status', 'active');

        if (error || !allMatches?.length) return;

        const matches = allMatches.filter((m) => {
          const isUser1 = m.user1_id === user.id;
          return isUser1 ? !m.user1_seen_at : !m.user2_seen_at;
        });
        if (matches.length === 0) return;

        const otherUserIds = matches.map((m) =>
          m.user1_id === user.id ? m.user2_id : m.user1_id
        );

        const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
        const { data: users } = await rpcPeerUsersForViewer(supabase, otherUserIds, {
          includePrefs: false
        });

        const userMap = new Map((users || []).map((u: any) => [u.id, u]));
        const queue: QueuedMatch[] = matches.map((m) => {
          const otherId = m.user1_id === user.id ? m.user2_id : m.user1_id;
          const u = userMap.get(otherId) as { name?: string; photo_urls?: string[] } | undefined;
          return {
            matchId: m.id,
            matchedUser: {
              id: otherId,
              name: u?.name || 'Someone',
              photo: u?.photo_urls?.[0] || ''
            }
          };
        });

        if (!isMounted) return;
        setUnseenQueue(queue);
        if (queue.length > 0) showMatch(queue[0].matchedUser, queue[0].matchId);
      } catch (e) {
        console.warn('[MatchNotification] Fetch unseen matches failed:', e);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user?.id, showMatch]);



  const [isStartingChat, setIsStartingChat] = useState(false);

  const handleStartChat = useCallback(async () => {
    if (!matchedUser || !user?.id || isStartingChat) return;

    try {
      setIsStartingChat(true);
      console.log('🎉 MATCH: Starting chat with', matchedUser.name);

      // Store matched user data before hiding celebration
      const matchedUserId = matchedUser.id;
      const matchedUserName = matchedUser.name;
      const matchIdToSave = currentMatchIdRef.current ?? undefined;

      // V323: Await DB update BEFORE navigate so it persists if component unmounts
      await markMatchAsSeen(matchIdToSave);
      hideMatchCelebration(matchIdToSave);

      // Navigate to chat using the proper chat navigation service
      const chatId = await navigateToFriendChat(
        user.id,
        matchedUserId,
        matchedUserName,
        navigate,
        { isMatch: true }
      );

      if (chatId) {
        console.log('✅ MATCH: Successfully navigated to chat:', chatId);
        if (import.meta.env.DEV) toast.success(`Chat Started! You can now chat with ${matchedUserName}`);
      } else {
        // If navigation failed, show error but don't re-show celebration
        toast.error('Failed to open chat. Please try again from the chat list.');
      }

    } catch (error) {
      console.error('❌ Failed to navigate to chat:', error);
      toast.error('Failed to open chat');
    } finally {
      setIsStartingChat(false);
    }
  }, [matchedUser, user?.id, markMatchAsSeen, hideMatchCelebration, navigate, isStartingChat]);

  const contextValue: MatchNotificationContextType = {
    showMatchCelebration,
    hideMatchCelebration,
    isMatchCelebrationVisible: showMatchNotification,
  };

  return (
    <MatchNotificationContext.Provider value={contextValue}>
      {children}
      
      {/* Global Match Celebration Modal */}
      <MatchCelebration
        isOpen={showMatchNotification}
        onClose={(id) => hideMatchCelebration(id)}
        onStartChat={handleStartChat}
        onKeepSwiping={(id) => hideMatchCelebration(id)}
        matchId={currentMatchId}
        matchedUser={{
          id: matchedUser?.id || '',
          name: matchedUser?.name || '',
          photo: matchedUser?.photo || ''
        }}
        currentUser={{
          id: user?.id || '',
          name: userProfile?.name || '',
          photo: userProfile?.photo_urls?.[0] || ''
        }}
        isStartingChat={isStartingChat}
      />
    </MatchNotificationContext.Provider>
  );
};
